import { AmbassadorDetail, AmbassadorMini, EventNotificationFrequencies } from '@omniafishing/core';
import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { LoadingState } from '../constants/loading_state';
import { AMBASSADOR_EXCLUDE_ALL, apiV1 } from '../lib/api';
import { errorHandler } from '../lib/error_handler';
import { hasAccessToken } from '../redux/auth';
import { UserProfileActions } from '../redux/user_profile';
import { useFlashMessage } from './use_flash_message';

interface EventNotifications {
  frequency: EventNotificationFrequencies;
  new_fishing_report: boolean;
  new_article: boolean;
}

interface AmbassadorArgs {
  ambassador: AmbassadorMini;
  event_notifications: EventNotifications;
  email?: string;
  onCompletion?: () => void;
  onResponse?: (userProfile: AmbassadorDetail) => void;
  onSuccess?: () => void;
  onError?: () => void;
}

export const useAmbassador = () => {
  const dispatch = useDispatch();
  const isLoggedIn = useSelector(hasAccessToken);
  const { setFlashMessage } = useFlashMessage();
  const [loadingState, setLoadingState] = useState<LoadingState>(LoadingState.NOT_STARTED);

  const updateAmbassadorNotifications = (args: AmbassadorArgs) => {
    const { ambassador, event_notifications, email, onCompletion, onResponse, onSuccess, onError } =
      args;
    const { slug } = ambassador;

    if (!isLoggedIn) {
      return;
    }
    setLoadingState(LoadingState.PENDING);
    apiV1
      .userEventNotificationsUpdate(slug, event_notifications, email, AMBASSADOR_EXCLUDE_ALL)
      .then(({ data }) => {
        setLoadingState(LoadingState.DONE);
        dispatch(UserProfileActions.USER_PROFILE_EVENT_NOTIFICATIONS_UPDATE_SUCCESS(data));
        setFlashMessage({
          header: 'Notifications Updated',
          subheader: `Your notification settings for ${ambassador.full_name} have been updated`,
        });
        onCompletion?.();
        onSuccess?.();
        onResponse?.(data.data);
      })
      .catch((err) => {
        setLoadingState(LoadingState.ERROR);
        errorHandler(`ERROR: updateUserNotifications: ${slug}`, err);
        onCompletion?.();
        onError?.();
      })
      .finally(() => {
        setLoadingState(LoadingState.NOT_STARTED);
        onCompletion?.();
      });
  };

  return { loadingState, updateAmbassadorNotifications };
};
